import { useCallback, useEffect, useRef, useState } from "react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Build the websocket URL from the configured backend origin.
function wsUrlFor(token) {
  if (!BACKEND) return null;
  try {
    const url = new URL(`/api/ws/tp/${token}`, BACKEND);
    url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
    return url.toString();
  } catch {
    return null;
  }
}

/**
 * Live sync channel for a teleprompter share-link token.
 *
 * - All connected peers (host + guests) receive every message sent by others.
 * - Pass `onEvent(payload)` to receive relayed events. The server tags its own
 *   housekeeping messages with `type: "peers"`.
 * - Call `.send(payload)` to broadcast. Payload is relayed verbatim.
 * - Pass `enabled=false` (or token falsy) to keep the socket closed.
 *
 * The hook auto-reconnects with linear backoff when the connection drops
 * unexpectedly.
 */
export default function useTeleprompterSync({ token, enabled = true, onEvent }) {
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState(0);
  const wsRef = useRef(null);
  const retryRef = useRef(0);
  const timerRef = useRef(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const connect = useCallback(() => {
    if (!token || !enabled) return;
    const url = wsUrlFor(token);
    if (!url) return;
    let ws;
    try {
      ws = new WebSocket(url);
    } catch {
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      retryRef.current = 0;
    };
    ws.onmessage = (ev) => {
      let data = null;
      try { data = JSON.parse(ev.data); } catch { return; }
      if (!data || typeof data !== "object") return;
      if (data.type === "peers" && typeof data.count === "number") {
        setPeers(data.count);
        return;
      }
      if (onEventRef.current) onEventRef.current(data);
    };
    ws.onclose = (ev) => {
      setConnected(false);
      wsRef.current = null;
      // 4404 = invalid token, 4410 = expired. Don't retry those.
      if (ev.code === 4404 || ev.code === 4410) return;
      if (!enabled) return;
      retryRef.current = Math.min(retryRef.current + 1, 8);
      const delay = 500 * retryRef.current;
      timerRef.current = setTimeout(connect, delay);
    };
    ws.onerror = () => { /* onclose will fire next */ };
  }, [token, enabled]);

  useEffect(() => {
    if (!enabled || !token) return;
    connect();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        try { ws.close(1000, "unmount"); } catch {}
      }
      setConnected(false);
      setPeers(0);
    };
  }, [token, enabled, connect]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch { return false; }
  }, []);

  return { connected, peers, send };
}
